import { ScrollArea } from '~/primitives/scroll-area';
import Flag from '~/partials/Flag';
import { cn, toCamelCase } from '~/util';
import { ListTabSwitch } from '~/partials/TabSwitch';
import ThemedImage from '~/partials/ThemedImage';
import { useTheme } from '~/components/Providers/ThemeContext';
import chroma from 'chroma-js';
import TooltipButton from '~/partials/Tooltip';
import { SuggestedLevel } from './-ProfileLevelSuggestion';
import { getRouteApi, Link } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import Load from '~/components/Load';
import { BanLevelTags } from '~/partials/Tags';
import { useAuth, useScopes } from '~/components/Providers/AuthProvider';
import { ChartColumn, ExternalLink, Info, Pencil } from 'lucide-react';
import { EditUserSheet } from './-EditUserSheet';
import { UserInfoDialog } from './-UserInfoDialog';
import { Button } from '~/primitives/button';
import { UserProfilePicture } from '~/components/UserProfilePicture';
import { BadgesDialog } from './-BadgesDialog';
import { UserPointDisplay, RankDisplay } from '../-shared';
import { ProfileBreakdowns } from './-ProfileBreakdowns';

const Route = getRouteApi('/profile/user/$userid');

function ProfileSummary() {
    const { profile, classicTotal, platformerTotal } = Route.useLoaderData({
        select: ({ profile, classicTotal, platformerTotal }) => ({
            profile,
            classicTotal,
            platformerTotal,
        }),
    });

    const isOwnProfile = useAuth()?.auth?.id === profile.id;

    const [editOpen, setEditOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [badgesOpen, setBadgesOpen] = useState(false);

    const { hasScope } = useScopes();

    const { primaryColor } = useTheme();
    const hue = chroma(primaryColor).get('hsl.h');
    const lightness = chroma(primaryColor).get('hsl.l') * 100 + 50;

    const showData = profile.ban_level <= 2 || hasScope(undefined);

    return (
        <div className="flex flex-col w-full lg:w-2/6 lg:px-2">
            <ScrollArea className="lg:p-4 lg:h-[calc(100vh-150px)]">
                <div className="flex flex-col items-center text-center w-full mt-4">
                    <UserProfilePicture
                        user={profile}
                        className="size-40 mb-2"
                        onBadgeClick={() => setBadgesOpen(true)}
                        showBadgeIfNull={
                            isOwnProfile || hasScope('user_modify')
                        }
                        smallBadge
                    />

                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {profile.country && profile.country != 0 ? (
                            <Link
                                className="transition-transform hover:scale-110 block w-max"
                                to={'/profile/country/$country'}
                                params={{ country: String(profile.country) }}
                                search={(prev) => ({ list: prev.list })}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Flag countryCode={profile.country} />
                            </Link>
                        ) : (
                            <></>
                        )}
                        <p className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-2xl-3xl-clamp] leading-[1.5!important] font-medium text-secondary-foreground">
                            {profile.global_name}
                        </p>
                        {profile.roles?.map((role) => {
                            if (role.hide) return null;

                            return (
                                <TooltipButton
                                    text={toCamelCase(role.role_desc)}
                                    key={role.role_desc}
                                >
                                    <img
                                        src={`/assets/staff-icons/${role.role_desc}.svg`}
                                        alt={`User role ${role.role_desc}`}
                                        className="h-8"
                                    />
                                </TooltipButton>
                            );
                        })}
                        {hasScope('user_modify') && (
                            <>
                                <Info
                                    className="h-7 w-7 text-secondary-foreground hover:text-secondary-light cursor-pointer transition-colors"
                                    onClick={() => setInfoOpen(true)}
                                />
                                <Pencil
                                    className="h-7 w-7 text-secondary-foreground hover:text-secondary-light cursor-pointer transition-colors"
                                    onClick={() => setEditOpen(true)}
                                />
                            </>
                        )}
                        {hasScope('reviewers_audit') && (
                            <Link
                                to="/staff/stats/$userid"
                                params={{ userid: profile.id }}
                                search={(prev) => ({ list: prev.list })}
                            >
                                <ChartColumn className="h-7 w-7 text-secondary-foreground hover:text-secondary-light cursor-pointer transition-colors" />
                            </Link>
                        )}
                    </div>
                    <span
                        className={cn(
                            'clamp-[text-xl-3xl-clamp] lg:clamp-[text-xl-2xl-clamp] leading-[1!important] text-primary uppercase',
                            profile?.clan?.tag &&
                                'hover:scale-110 transition-all',
                        )}
                    >
                        {profile.clan?.tag ? (
                            <>
                                <Link
                                    to={`/profile/clan/$clanid`}
                                    params={{ clanid: profile?.clan?.id }}
                                >
                                    Clan: {profile?.clan?.tag}
                                </Link>
                            </>
                        ) : (
                            <>Clan: None</>
                        )}
                    </span>
                    {profile.ban_level > 0 && (
                        <div className="my-1">
                            <BanLevelTags item={profile.ban_level} />
                        </div>
                    )}
                </div>

                <p className="text-center text-secondary-light mt-2">
                    {profile?.description && profile.description.length > 0
                        ? profile.description
                        : "This user hasn't provided a description yet."}
                </p>
                {hasScope('submission_edit_non_self_claimed') && (
                    <div className="flex flex-wrap gap-2 w-full justify-center">
                        <Link
                            to="/staff/submissions"
                            search={({ list }) => ({
                                list,
                                submitter: {
                                    id: profile.id,
                                    global_name: profile.global_name,
                                    username: profile.username,
                                },
                            })}
                        >
                            <Button
                                className="w-full gap-2"
                                variant="secondary"
                            >
                                Submissions <ExternalLink />
                            </Button>
                        </Link>
                        <Link
                            to="/staff/records"
                            search={({ list }) => ({
                                list,
                                submitter: {
                                    id: profile.id,
                                    global_name: profile.global_name,
                                    username: profile.username,
                                },
                            })}
                        >
                            <Button
                                className="w-full gap-2"
                                variant="secondary"
                            >
                                Records <ExternalLink />
                            </Button>
                        </Link>
                        {hasScope('reviewers_audit') && (
                            <Link
                                to="/staff/submissions"
                                search={({ list }) => ({
                                    list,
                                    reviewer: {
                                        id: profile.id,
                                        global_name: profile.global_name,
                                        username: profile.username,
                                    },
                                })}
                            >
                                <Button
                                    className="w-full gap-2"
                                    variant="secondary"
                                >
                                    Reviewed <ExternalLink />
                                </Button>
                            </Link>
                        )}
                    </div>
                )}

                <ListTabSwitch
                    from="/profile/user/$userid"
                    className="w-10/12 mx-auto"
                    classicDisplay={`Classic (${showData ? classicTotal : 0})`}
                    platformerDisplay={`Platformer (${showData ? platformerTotal : 0})`}
                    size="md"
                />

                <div className="flex justify-center items-center mt-6 gap-2">
                    <UserPointDisplay
                        label="Level Points"
                        value={
                            (profile?.rank?.total_points -
                                profile?.rank?.pack_points) /
                            10
                        }
                    />
                    <UserPointDisplay
                        label="Total Points"
                        value={profile?.rank?.total_points / 10}
                    />
                    <UserPointDisplay
                        label="Pack Points"
                        value={profile?.rank?.pack_points / 10}
                    />
                </div>

                <div className="mt-8 text-center clamp-[px-3-6-clamp]">
                    <h3 className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-lg-2xl-clamp] font-semibold uppercase">
                        <ThemedImage
                            src="/assets/globalRank.webp"
                            alt="global rank icon"
                            className="inline h-6 mr-2"
                            hueRotation={hue}
                            lightnessCorrection={lightness}
                        />
                        Global Rankings
                    </h3>
                    <div className="mt-2">
                        <RankDisplay
                            label="Points Rank"
                            value={profile?.rank?.raw_rank}
                        />
                        <RankDisplay
                            label="Points Rank (with packs)"
                            value={profile?.rank?.rank}
                        />
                        <RankDisplay
                            label="Extremes Count Rank"
                            value={profile?.rank?.extremes_rank}
                        />
                        <RankDisplay
                            label="Hardest Level Rank"
                            value={profile?.rank?.hardest_rank}
                        />
                    </div>
                </div>

                <div className="mt-8 text-center clamp-[px-3-6-clamp]">
                    <h3 className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-lg-2xl-clamp] font-semibold uppercase">
                        <ThemedImage
                            src="/assets/countryRank.webp"
                            alt="country rank icon"
                            className="inline h-6 mr-2"
                            hueRotation={hue}
                            lightnessCorrection={lightness}
                        />
                        Country Rankings
                    </h3>
                    <div className="mt-2">
                        <RankDisplay
                            label="Points Rank"
                            value={
                                profile?.country
                                    ? profile?.rank?.country_raw_rank
                                    : undefined
                            }
                        />
                        <RankDisplay
                            label="Points Rank (with packs)"
                            value={
                                profile?.country
                                    ? profile?.rank?.country_rank
                                    : undefined
                            }
                        />
                        <RankDisplay
                            label="Extremes Count Rank"
                            value={
                                profile?.country
                                    ? profile?.rank?.country_extremes_rank
                                    : undefined
                            }
                        />
                        <RankDisplay
                            label="Hardest Level Rank"
                            value={
                                profile?.country
                                    ? profile?.rank?.country_hardest_rank
                                    : undefined
                            }
                        />
                    </div>
                </div>

                <ProfileBreakdowns />
                <div className="mt-8 text-center clamp-[px-3-6-clamp]">
                    <h3 className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-lg-2xl-clamp] font-semibold uppercase">
                        <ThemedImage
                            src="/assets/lightbulbIcon.webp"
                            alt="Recommended Level"
                            className="inline h-10 mr-3"
                            hueRotation={hue}
                            lightnessCorrection={lightness}
                        />
                        Recommended Level
                    </h3>
                    <Suspense fallback={<Load />}>
                        <SuggestedLevel />
                    </Suspense>
                </div>
            </ScrollArea>
            <BadgesDialog open={badgesOpen} onOpenChange={setBadgesOpen} />
            {hasScope('user_modify') && (
                <>
                    <EditUserSheet open={editOpen} onOpenChange={setEditOpen} />
                    <UserInfoDialog
                        open={infoOpen}
                        onOpenChange={setInfoOpen}
                    />
                </>
            )}
        </div>
    );
}

export default ProfileSummary;
